import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { css, keyframes, type Theme } from '@emotion/react';
import { Plus, RocketLaunch, Square, Triangle, Check, Sparkle } from '@phosphor-icons/react';
import type { ComponentDocument } from 'common/stack/ComponentDocuments';
import { parsePackageUrl } from 'common/stack/stackUtils';
import { trpc, useMutation, useQuery } from 'client/lib/trpc';
import { useAccount, useUser } from 'client/lib/User';
import { parseIdentifier } from 'client/pages/stack/stackUtils';
import { Button } from 'client/components/Button';
import { EditAppSaveButton } from 'client/components/EditApp/EditAppSaveButton';
import { ComponentsAdd } from 'client/components/Icons/ComponentsAdd';
import { createStackBackgroundUri } from 'client/components/Icons/StackBackground';
import { StackComplete } from 'client/components/Icons/StackComplete';
import { StackFull } from 'client/components/Icons/StackFull';
import { Actions, Section as SettingsSection } from 'client/components/Layout/Settings';
import { ListItemFigure } from 'client/components/Other/ListItemFigure';
import { Section } from 'client/components/Section/Section';
import { Stack } from 'client/components/Stack';
import { Tabs } from 'client/components/Tabs/Tabs';
import { Tag } from 'client/components/Tag';
import { Typography } from 'client/components/Typography';
import { layoutStyles, navbarHeight } from 'client/Theme';
import { StackAddonsDialog } from './components/StackAddonsDialog';
import { StackAddonSelect } from './components/StackAddonSelect';
import { StackComponentsDialog } from './components/StackComponentsDialog';
import { StackComponentSelect } from './components/StackComponentSelect';
import { StackFiles } from './components/StackFiles';
import { StackRepository } from './components/StackRepository';
import { StackServices } from './components/StackServices';

type Component = {
  identifier: string;
  package: string;
  type: 'component' | 'addon';
  details: ComponentDocument;
};

export const StackBuilderPage = () => {
  const navigate = useNavigate();
  const account = useAccount();
  const { updateSession } = useUser();

  const [stack, setStack] = useState<Component[]>([]);
  const [componentsTab, setComponentsTab] = useState<'stacks' | 'components'>('components');
  const [openDialog, setOpenDialog] = useState<'template' | 'addon' | null>(null);
  const [repository, setRepository] = useState<{ owner: string; name: string; isPrivate: boolean } | null>(null);

  const componentsQuery = useQuery(trpc.stack.listComponents, { listing: 'overview', accountID: account!.id }, { enabled: !!account?.id });

  const createMutation = useMutation(trpc.project.create, {
    onSuccess: (data) => {
      if (data.status !== 'ok') return;
      updateSession();
      if (data.deploymentID) {
        navigate(`../project/${data.projectID}/deployment/${data.deploymentID}`);
      } else {
        navigate(`../project/${data.projectID}`);
      }
      window.scrollTo(0, 0);
    },
  });

  const errorMessage = useMemo(
    () =>
      createMutation.data?.status && createMutation.data.status !== 'ok' ? (
        <>
          Failed to create stack
          <Typography variant="fineprint">{createMutation.data.status}</Typography>
        </>
      ) : undefined,
    [createMutation.data?.status]
  );

  const componentsConfig = stack
    .filter((item) => item.type === 'component')
    .flatMap((item) => [
      {
        name: item.details.name,
        identifier: item.identifier,
        package: item.package,
      },
    ]);

  const addonsConfig = stack
    .filter((item) => item.type === 'addon')
    .flatMap((item) => [
      {
        name: item.details.name,
        identifier: item.identifier,
        package: item.package,
      },
    ]);

  const handleDialogClose = () => setOpenDialog(null);

  const handleStackAdd = useCallback(
    (component: Component) =>
      setStack((stack) => [...stack, { ...component, identifier: parseIdentifier({ identifier: component.identifier, stack }) }]),
    [setStack]
  );

  const handleStackRemove = useCallback(
    (url: string) => setStack((stack) => stack.filter((component) => parsePackageUrl(component.package).url !== url)),
    [setStack]
  );

  const handleIdentifierChange = useCallback(({ identifier, value }: { identifier: string; value: string }) => {
    setStack((stack) => {
      if (identifier === value) return stack;

      const component = stack.find((component) => component.identifier === identifier);
      if (!component) return stack;

      component.identifier = parseIdentifier({ identifier: value, stack });
      return [...stack];
    });
  }, []);

  const handleVersionChange = useCallback(({ identifier, value }: { identifier: string; value: string }) => {
    setStack((stack) => {
      const component = stack.find((component) => component.identifier === identifier);
      if (!component) return stack;

      component.package = `${component.package.split('#')[0]}#${value}`;
      return [...stack];
    });
  }, []);

  const createStackConfig = () => {
    // const template = stack.find((component) => component.details.components) || null;

    const config = {
      diploiVersion: 'v1.0' as const,
      template: undefined as string | undefined,
      components: componentsConfig.map((config) => ({
        ...config,
        env: {
          include: addonsConfig.map((addon) => `${addon.identifier}.*`),
        },
      })),
      addons: addonsConfig,
    };

    // if (template) {
    //   config.template = template.package;
    // }

    return config;
  };

  const handleLaunch = async () => {
    const stackConfig = createStackConfig();
    if (!account) return;
    createMutation.mutate({ accountID: account.id, stackConfig, repository });
  };

  const tabs = [
    { id: 'stacks', title: 'Pre-Configured Stacks', icon: <StackComplete /> },
    {
      id: 'components',
      title: 'Mix & Match Components',
      icon: <ComponentsAdd />,
    },
  ];

  return (
    <Stack gap={64} css={stackStyle}>
      <div css={backgroundStyle} />
      <header css={headerStyle}>
        <div>
          <Typography variant="h1">
            <Tag>
              <Sparkle weight="fill" /> New
            </Tag>
            Stack
            <br />
            Builder
            {/* Kitchen */}
          </Typography>
          {/* <Typography>You can cook up a complex tech stack in seconds with our dead-simple stack builder.</Typography> */}
          <Typography>Create a complex tech stack in seconds with our dead-simple stack builder.</Typography>
        </div>

        <svg width="327" height="250" viewBox="0 0 327 250" fill="none" xmlns="http://www.w3.org/2000/svg" overflow="visible">
          <path
            d="M214 178.493L166.85 169.499C162.51 168.671 158.321 171.518 157.493 175.858L148.499 223.008C147.671 227.348 150.518 231.538 154.858 232.365L202.008 241.36C206.348 242.187 210.538 239.34 211.366 235L220.36 187.85C221.188 183.51 218.34 179.321 214 178.493Z"
            stroke="#DC5EA8"
            strokeWidth="16"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{ animationDelay: '0ms' }}
          />
          <path
            d="M55.4898 9.74411L13.07 32.2065C9.16533 34.2741 7.67612 39.1155 9.74373 43.0202L32.2061 85.44C34.2737 89.3446 39.1151 90.8339 43.0198 88.7662L85.4396 66.3039C89.3443 64.2363 90.8335 59.3948 88.7659 55.4902L66.3035 13.0704C64.2359 9.16572 59.3944 7.67651 55.4898 9.74411Z"
            stroke="#DC5EA8"
            strokeWidth="16"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{ animationDelay: '400ms' }}
          />
          <path
            d="M298.79 26.452L308.658 85.3029C309.343 89.446 305.52 93.0258 301.716 91.7749L248.982 74.436C245.177 73.185 244.225 68.0351 247.235 65.1072L290.102 23.5952C293.192 20.5983 298.082 22.2061 298.79 26.452Z"
            stroke="#6650FA"
            strokeWidth="16"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{ animationDelay: '900ms' }}
          />
        </svg>
      </header>

      <Section
        title={
          <>
            <ListItemFigure size={26} icon={<Square />} ornament={<Plus weight="bold" />} /> {/* Pick an Entrée */}
            Pick Components
          </>
        }
        subtitle="Base blocks of your stack. All code is editable."
        css={sectionStyle}
      >
        <nav css={tabsStyle}>
          <Tabs items={tabs} selected={componentsTab} onSelect={(item) => setComponentsTab(item.id as 'stacks' | 'components')} />
        </nav>
        <StackComponentSelect
          tab={componentsTab}
          stack={stack}
          components={componentsQuery.data?.components || []}
          onAdd={handleStackAdd}
          onRemove={handleStackRemove}
        />
        {/* <Button onClick={() => setOpenDialog('template')}>Browse All Components</Button> */}
      </Section>

      <Section
        title={
          <>
            <ListItemFigure size={26} icon={<Triangle />} ornament={<Plus weight="bold" />} />
            {/* Something to the Side? */}
            Pick Add-ons
          </>
        }
        subtitle="Complementary services. Configurable with config."
        css={sectionStyle}
      >
        <StackAddonSelect
          stack={stack}
          components={componentsQuery.data?.components || []}
          onAdd={handleStackAdd}
          onRemove={handleStackRemove}
        />
        {/* <Button onClick={() => setOpenDialog('addon')}>Browse All Add-Ons</Button> */}
      </Section>

      <Section
        title={
          <>
            <ListItemFigure size={26} icon={<StackFull />} ornament={<Check weight="bold" />} /> Putting It All Together
          </>
        }
        subtitle="Preview your stack monorepo. You can switch things up by naming components in a more meaningful way."
        css={[sectionStyle, configStyle]}
      >
        <StackFiles
          components={componentsConfig}
          stack={stack}
          onIdentifier={handleIdentifierChange}
          onVersion={handleVersionChange}
          onRemove={handleStackRemove}
        />
        <StackServices
          addons={addonsConfig}
          stack={stack}
          onIdentifier={handleIdentifierChange}
          onVersion={handleVersionChange}
          onRemove={handleStackRemove}
        />
      </Section>

      <SettingsSection title="Repository" subtitle="Configure the Git repositories this project pulls its code from.">
        <StackRepository repository={repository} onChange={setRepository} />
      </SettingsSection>

      {/* <Section title="Debug">
        <pre style={{ color: 'white' }}>{createConfigYaml()}</pre>

        <div>
          <Button onClick={handleDownload}>Download HELM Archive</Button>
          <Button onClick={handleSave}>Save as a Stack</Button>
        </div>
      </Section> */}

      <Actions>
        <EditAppSaveButton
          icon={<RocketLaunch weight="regular" />}
          primary
          large
          loading={createMutation.isLoading}
          error={errorMessage}
          onClick={handleLaunch}
        >
          Launch Stack
        </EditAppSaveButton>
      </Actions>

      <StackComponentsDialog open={openDialog === 'template'} onClose={handleDialogClose} onSelect={() => {}} />
      <StackAddonsDialog open={openDialog === 'addon'} onClose={handleDialogClose} onSelect={() => {}} />
    </Stack>
  );
};

const stackStyle = css`
  ${layoutStyles}
`;

const headerBackground = createStackBackgroundUri({ gap: 12, primary: 'rgba(0, 0, 0, 0.1)', secondary: 'rgba(0, 0, 0, 0.1)' });

const backgroundAnimation = keyframes`
  0% {
    background-position: 0 0;
  }
  100% {
    background-position: 128px 128px;
  }
`;

const backgroundStyle = css`
  position: absolute;
  display: flex;
  justify-content: center;
  align-items: center;
  top: ${navbarHeight}px;
  left: 0;
  width: 100%;
  height: 500px;
  z-index: 0;
  overflow: hidden;

  mask-image: linear-gradient(rgba(255, 255, 255, 1) 40%, transparent);

  &::after {
    position: absolute;
    content: '';
    display: block;
    width: 120%;
    height: 180%;
    background-image: ${headerBackground};
    background-size: 256px;
    transform: rotate(8deg);

    animation: ${backgroundAnimation} 20s linear infinite;

    @media (prefers-reduced-motion) {
      animation: none;
    }
  }
`;

const shapeAnimation = keyframes`
  0% {
    transform: translateY(0px);
  }
  100% {
    transform: translateY(15px);
  }
`;

const headerStyle = (theme: Theme) => css`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 64px;
  margin-bottom: 32px;
  z-index: 1;

  .typography.h1 {
    margin: 24px 0;
    font-size: 4em;
    font-weight: 900;
    font-family: 'Montserrat', ${theme.font.header};
    line-height: 1.05;

    > .tag {
      position: absolute;
      gap: 4px;
      margin-top: -26px;
      background-color: ${theme.color.primary[100]} !important;
    }
  }

  .typography.body1 {
    margin-top: -10px;
    max-width: 350px;
    color: ${theme.color.grey[100]};
  }

  > svg {
    scale: 0.8;

    > path {
      animation: ${shapeAnimation} 3s ease-in-out infinite;
      animation-direction: alternate;

      @media (prefers-reduced-motion) {
        animation: none;
      }
    }
  }
`;

const sectionStyle = css`
  > header {
    &::before {
      content: none;
    }

    h6 {
      display: flex;
      align-items: center;
      gap: 8px;

      > figure {
        transform: translateY(-3px);
      }
    }
  }
`;

const tabsStyle = (theme: Theme) => css`
  margin-bottom: 8px;
  border-bottom: 2px solid ${theme.color.grey[60]};

  > div {
    margin-bottom: -2px;
  }
`;

const configStyle = css`
  > div {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    grid-gap: 32px;
  }
`;
